import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Font,
} from "@react-pdf/renderer";
import fontBold from "../assests/fonts/NotoSansTamil-Bold.ttf";
import fontRegular from "../assests/fonts/NotoSansTamil-Regular.ttf";

// 2. You MUST register the fonts to use them in styles
Font.register({
  family: "NotoSansTamil",
  fonts: [
    { src: fontRegular, fontWeight: "normal" },
    { src: fontBold, fontWeight: "bold" },
  ],
});
const styles = StyleSheet.create({
  page: {
    paddingTop: 25,
    paddingBottom: 60,
    paddingHorizontal: 40,
    fontFamily: "NotoSansTamil",
    fontSize: 10,
    backgroundColor: "#ffffff",
  },

  // Header
  header: {
    marginBottom: 1,
    textAlign: "center",
  },

  companyName: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#1e40af",
    letterSpacing: 1.2,
  },
  reportTitle: {
    fontSize: 14,
    marginTop: 8,
    color: "#1e293b",
    fontWeight: "bold",
  },
  dateRange: {
    fontSize: 10,
    color: "#475569",
    marginTop: 6,
    fontWeight: "bold",
  },

  // Table
  table: {
    width: "100%",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    overflow: "hidden",
    marginTop: 5,
  },
  headerRow: {
    backgroundColor: "#1e40af",
    color: "white",
  },
  row: {
    flexDirection: "row",
    borderBottom: "1px solid #e2e8f0",
  },
  rowAlt: {
    backgroundColor: "#f8fafc",
  },
  cell: {
    paddingVertical: 6,
    paddingHorizontal: 4,
    textAlign: "center",
    fontSize: 10.5,
  },
  cellLeft: { textAlign: "left", paddingLeft: 15 },
  cellRight: { textAlign: "right", paddingRight: 15 },
  headerCell: {
    fontWeight: "bold",
    fontSize: 11,
    paddingVertical: 7,
    fontFamily: "Times-Roman",
  },

  // Total Row
  totalRow: {
    backgroundColor: "#1e3a8a",
    color: "white",
    fontWeight: "bold",
    fontSize: 12,
  },

  // Footer
  footer: {
    position: "absolute",
    bottom: 20,
    left: 40,
    right: 40,
    textAlign: "center",
    fontSize: 9,
    color: "#64748b",
    borderTop: "1px solid #e2e8f0",
    paddingTop: 10,
  },
});

const AttendancePDF = ({ data, fromDate, toDate }) => {
  const totalFullDays = data.reduce((s, i) => s + (i.fullDays || 0), 0) || 0;
  const totalHalfDays = data.reduce((s, i) => s + (i.halfDays || 0), 0) || 0;
  const totalPresent = data.reduce((s, i) => s + (i.presentDays || 0), 0) || 0;
  const totalAbsent = data.reduce((s, i) => s + (i.absentDays || 0), 0) || 0;
  const grandTotal = data.reduce((s, i) => s + (i.totalSalary || 0), 0) || 0;

  const formatDate = (d) => {
    if (!d) return "All Time";
    return new Date(d).toLocaleDateString("en-GB"); // DD/MM/YYYY
  };

  return (
    <Document>
      <Page size="A4" orientation="landscape" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.companyName}>MSK</Text>
          <Text style={styles.reportTitle}>
            Monthly Staff Attendance & Salary Report
          </Text>
          <Text style={styles.dateRange}>
            Period: {formatDate(fromDate)} To {formatDate(toDate)}
          </Text>
        </View>

        {/* Table */}
        <View style={styles.table}>
          {/* Header */}
          <View style={[styles.row, styles.headerRow]} fixed>
            <Text style={[styles.cell, styles.headerCell, { width: 50 }]}>
              S.No
            </Text>
            <Text
              style={[
                styles.cell,
                styles.headerCell,
                styles.cellLeft,
                { flex: 3 },
              ]}
            >
              Staff Name
            </Text>
            <Text style={[styles.cell, styles.headerCell, { width: 80 }]}>
              Full Days
            </Text>
            <Text style={[styles.cell, styles.headerCell, { width: 80 }]}>
              Half Days
            </Text>
            <Text style={[styles.cell, styles.headerCell, { width: 80 }]}>
              Present
            </Text>
            <Text style={[styles.cell, styles.headerCell, { width: 80 }]}>
              Absent
            </Text>
            <Text
              style={[
                styles.cell,
                styles.headerCell,
                styles.cellRight,
                { width: 110 },
              ]}
            >
              Salary
            </Text>
          </View>

          {/* Data Rows */}
          {data.map((item, i) => (
            <View key={i} style={[styles.row, i % 2 === 1 && styles.rowAlt]}>
              <Text style={[styles.cell, { width: 50 }]}>{i + 1}</Text>
              <Text style={[styles.cell, styles.cellLeft, { flex: 3 }]}>
                {item.staffName}
              </Text>
              <Text style={[styles.cell, { width: 80 }]}>
                {Number(item.fullDays || 0).toFixed(1)}
              </Text>
              <Text style={[styles.cell, { width: 80 }]}>
                {Number(item.halfDays || 0).toFixed(1)}
              </Text>
              <Text style={[styles.cell, { width: 80, color: "#16a34a" }]}>
                {Number(item.presentDays || 0).toFixed(1)}
              </Text>
              <Text style={[styles.cell, { width: 80, color: "#dc2626" }]}>
                {Number(item.absentDays || 0).toFixed(1)}
              </Text>
              <Text style={[styles.cell, styles.cellRight, { width: 110 }]}>
                Rs.{Number(item.totalSalary || 0).toFixed(2)}
              </Text>
            </View>
          ))}

          {/* Grand Total */}
          <View style={[styles.row, styles.totalRow]}>
            <Text style={[styles.cell, { width: 50 }]}></Text>
            <Text style={[styles.cell, styles.cellLeft, { flex: 3 }]}>
              GRAND TOTAL
            </Text>
            <Text style={[styles.cell, { width: 80 }]}>
              {Number(totalFullDays).toFixed(1)}
            </Text>
            <Text style={[styles.cell, { width: 80 }]}>
              {Number(totalHalfDays).toFixed(1)}
            </Text>
            <Text style={[styles.cell, { width: 80 }]}>
              {Number(totalPresent).toFixed(1)}
            </Text>
            <Text style={[styles.cell, { width: 80 }]}>
              {Number(totalAbsent).toFixed(1)}
            </Text>
            <Text style={[styles.cell, styles.cellRight, { width: 110 }]}>
              Rs.{Number(grandTotal).toFixed(2)}
            </Text>
          </View>
        </View>

        {/* Footer */}
        <View style={styles.footer} fixed>
          <Text>
            Generated on {new Date().toLocaleDateString("en-GB")} | Vignesh
            Traders | Confidential
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export default AttendancePDF;
